/**
 * Owner 결제 안내 페이지
 * - 플랜 신청 후 입금 안내
 * - 입금 완료 알림
 * - 신청 취소
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BanknotesIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';

// 신청 상태 타입
type RequestStatus = 'pending' | 'payment_notified' | 'approved' | 'rejected' | 'cancelled';

// 플랜 신청 상태 응답 타입
interface PlanRequestStatus {
  request_id: string;
  plan_name: string;
  plan_price: number;
  status: RequestStatus;
  created_at: string;
  payment_notified_at?: string;
  approved_at?: string;
  depositor_name?: string;
  deposited_amount?: number;
}

// 결제 안내 정보
const PAYMENT_INFO = {
  bank: '신한은행',
  accountNumber: '110-123-456789',
  accountHolder: '(주)뉴어텐드',
};

const PaymentGuidePage: React.FC = () => {
  // 상태 관리
  const [requestStatus, setRequestStatus] = useState<PlanRequestStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 입금 완료 알림 폼 상태
  const [depositorName, setDepositorName] = useState('');
  const [depositedAmount, setDepositedAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // 취소 확인 모달 상태
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // API 호출을 위한 토큰 가져오기
  const getAuthToken = () => {
    return localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  };

  // 신청 상태 조회
  const fetchRequestStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/v1/owner/plan-request/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        // 신청 내역이 없는 경우
        setRequestStatus(null);
        return;
      }

      if (!response.ok) {
        throw new Error('신청 상태 조회에 실패했습니다.');
      }

      const data = await response.json();
      setRequestStatus(data);

      // 기존 입금자명, 입금 금액이 있으면 설정
      if (data.depositor_name) {
        setDepositorName(data.depositor_name);
      }
      if (data.deposited_amount) {
        setDepositedAmount(data.deposited_amount.toString());
      }
    } catch (err: any) {
      console.error('신청 상태 조회 오류:', err);
      setError(err.message || '신청 상태를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 컴포넌트 마운트 시 상태 조회
  useEffect(() => {
    fetchRequestStatus();
  }, [fetchRequestStatus]);

  // 입금 완료 알림
  const handleNotifyPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!depositorName.trim()) {
      setError('입금자명을 입력해주세요.');
      return;
    }

    if (!depositedAmount || parseInt(depositedAmount) <= 0) {
      setError('입금 금액을 입력해주세요.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/v1/owner/plan-request/notify-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          depositor_name: depositorName.trim(),
          deposited_amount: parseInt(depositedAmount),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || '입금 완료 알림에 실패했습니다.');
      }

      setSuccess('입금 완료 알림이 전송되었습니다. 관리자 확인 후 플랜이 활성화됩니다.');

      // 상태 새로고침
      await fetchRequestStatus();
    } catch (err: any) {
      console.error('입금 완료 알림 오류:', err);
      setError(err.message || '입금 완료 알림에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  // 신청 취소
  const handleCancelRequest = async () => {
    if (!requestStatus) return;

    try {
      setCancelling(true);
      setError(null);

      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/v1/owner/plan-request/${requestStatus.request_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || '신청 취소에 실패했습니다.');
      }

      setSuccess('플랜 신청이 취소되었습니다.');
      setShowCancelModal(false);
      setRequestStatus(null);
    } catch (err: any) {
      console.error('신청 취소 오류:', err);
      setError(err.message || '신청 취소에 실패했습니다.');
    } finally {
      setCancelling(false);
    }
  };

  // 상태별 배지 렌더링
  const renderStatusBadge = (status: RequestStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="h-4 w-4 mr-1" />
            입금 대기중
          </span>
        );
      case 'payment_notified':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <ClockIcon className="h-4 w-4 mr-1" />
            관리자 확인중
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            승인 완료
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
            반려됨
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <XMarkIcon className="h-4 w-4 mr-1" />
            취소됨
          </span>
        );
      default:
        return null;
    }
  };

  // 로딩 상태
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  // 신청 내역이 없는 경우
  if (!requestStatus) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <BanknotesIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              진행 중인 플랜 신청이 없습니다
            </h2>
            <p className="text-gray-600 mb-6">
              플랜을 신청하시면 이 페이지에서 결제 안내를 확인하실 수 있습니다.
            </p>
            <a
              href="/owner/settings"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              플랜 신청하기
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 페이지 헤더 */}
      <div className="bg-white border-b border-gray-200 mb-6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-bold text-gray-900">결제 안내</h1>
          <p className="mt-1 text-sm text-gray-500">
            플랜 신청 후 결제를 진행해주세요
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* 에러 메시지 */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* 성공 메시지 */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        )}

        {/* 신청 정보 카드 */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">신청 정보</h2>
              {renderStatusBadge(requestStatus.status)}
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">신청 플랜:</span>
                <span className="ml-2 font-medium text-gray-900">{requestStatus.plan_name}</span>
              </div>
              <div>
                <span className="text-gray-500">신청 금액:</span>
                <span className="ml-2 font-medium text-blue-600">
                  {requestStatus.plan_price.toLocaleString()}원
                </span>
              </div>
              <div>
                <span className="text-gray-500">신청일:</span>
                <span className="ml-2 font-medium text-gray-900">
                  {new Date(requestStatus.created_at).toLocaleDateString('ko-KR')}
                </span>
              </div>
              {requestStatus.approved_at && (
                <div>
                  <span className="text-gray-500">승인일:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {new Date(requestStatus.approved_at).toLocaleDateString('ko-KR')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 상태별 컨텐츠 */}
        {requestStatus.status === 'pending' && (
          <>
            {/* 결제 안내 카드 */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm mb-6">
              <div className="px-6 py-4 border-b border-blue-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <BanknotesIcon className="h-5 w-5 mr-2 text-blue-600" />
                  입금 안내
                </h2>
              </div>
              <div className="px-6 py-6">
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-blue-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-500 mb-1">입금 은행</div>
                        <div className="text-lg font-semibold text-gray-900">{PAYMENT_INFO.bank}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">계좌번호</div>
                        <div className="text-lg font-semibold text-gray-900 font-mono">
                          {PAYMENT_INFO.accountNumber}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">예금주</div>
                        <div className="text-lg font-semibold text-gray-900">{PAYMENT_INFO.accountHolder}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 mb-1">입금 금액</div>
                        <div className="text-lg font-bold text-blue-600">
                          {requestStatus.plan_price.toLocaleString()}원
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">입금 시 주의사항</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>입금자명은 신청자명과 동일하게 해주세요.</li>
                          <li>입금 후 아래 폼을 통해 입금 완료 알림을 보내주세요.</li>
                          <li>확인까지 1-2 영업일이 소요될 수 있습니다.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 입금 완료 알림 폼 */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">입금 완료 알림</h2>
                <p className="text-sm text-gray-500 mt-1">
                  입금 후 아래 정보를 입력해주세요
                </p>
              </div>
              <form onSubmit={handleNotifyPayment} className="px-6 py-6 space-y-4">
                <div>
                  <label htmlFor="depositorName" className="block text-sm font-medium text-gray-700 mb-2">
                    입금자명 *
                  </label>
                  <input
                    type="text"
                    id="depositorName"
                    value={depositorName}
                    onChange={(e) => setDepositorName(e.target.value)}
                    placeholder="입금자명을 입력하세요"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="depositedAmount" className="block text-sm font-medium text-gray-700 mb-2">
                    입금 금액 *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="depositedAmount"
                      value={depositedAmount}
                      onChange={(e) => setDepositedAmount(e.target.value)}
                      placeholder="입금 금액을 입력하세요"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="1"
                      required
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500">원</span>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? '처리 중...' : '입금 완료 알림'}
                </button>
              </form>
            </div>

            {/* 신청 취소 버튼 */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4">
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full px-6 py-3 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
                >
                  신청 취소
                </button>
              </div>
            </div>
          </>
        )}

        {requestStatus.status === 'payment_notified' && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="px-6 py-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                관리자 확인 중입니다
              </h3>
              <p className="text-gray-600 mb-4">
                입금 확인 후 플랜이 활성화됩니다.
                <br />
                확인까지 1-2 영업일이 소요될 수 있습니다.
              </p>
              {requestStatus.depositor_name && (
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-left max-w-sm mx-auto">
                  <div className="mb-2">
                    <span className="text-gray-500">입금자명:</span>
                    <span className="ml-2 font-medium text-gray-900">{requestStatus.depositor_name}</span>
                  </div>
                  {requestStatus.deposited_amount && (
                    <div>
                      <span className="text-gray-500">입금 금액:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {requestStatus.deposited_amount.toLocaleString()}원
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {requestStatus.status === 'approved' && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="px-6 py-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                결제가 완료되었습니다
              </h3>
              <p className="text-gray-600 mb-6">
                {requestStatus.plan_name} 플랜이 활성화되었습니다.
                <br />
                뉴어텐드 서비스를 이용해주셔서 감사합니다.
              </p>
              <a
                href="/owner/dashboard"
                className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors"
              >
                대시보드로 이동
              </a>
            </div>
          </div>
        )}
      </div>

      {/* 취소 확인 모달 */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* 배경 오버레이 */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setShowCancelModal(false)}
            />

            {/* 모달 컨텐츠 */}
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    신청 취소
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      정말로 플랜 신청을 취소하시겠습니까?
                      <br />
                      이미 입금하신 경우 환불 처리가 필요합니다.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleCancelRequest}
                  disabled={cancelling}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm disabled:bg-gray-400"
                >
                  {cancelling ? '취소 중...' : '신청 취소'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  돌아가기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGuidePage;
